import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Tag,
  message,
  Popconfirm,
  Upload,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  UploadOutlined,
  DownloadOutlined,
  PrinterOutlined,
  SearchOutlined,
  ReloadOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import colors from "../assets/styles/colour";
import * as XLSX from "xlsx";
import dataService from "../networks/api/service/dataService";
import CustomTable from "../components/TableComponent/CustomTable";

const { Option } = Select;
const { TextArea } = Input;

/* ─── Design tokens ─────────────────────────────────────── */
const T = {
  orange:     "#F97316",
  orangeLight:"#FFF7ED",
  orangeDark: "#C2500A",
  dark:       "#0F172A",
  darkCard:   "#1E293B",
  mid:        "#334155",
  muted:      "#64748B",
  border:     "#E2E8F0",
  white:      "#FFFFFF",
  success:    "#22C55E",
  danger:     "#EF4444",
};

/* ─── Injected global styles ─────────────────────────────── */
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500&display=swap');

  .emp-page * { box-sizing: border-box; }

  .emp-page {
    font-family: 'DM Sans', sans-serif;
    background: #F8FAFC;
    min-height: 100vh;
    color: ${T.dark};
  }

  /* Header */
  .emp-header {
    background: ${T.white};
    padding: 20px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 3px solid ${T.orange};
  }
  .emp-header-title {
    font-family: 'Syne', sans-serif;
    font-size: 26px;
    font-weight: 800;
    color: ${T.orangeDark};
    margin: 0;
    letter-spacing: -0.5px;
  }
  .emp-header-title span { color: ${T.orange}; }
  .emp-header-badge {
    background: ${T.orange};
    color: ${T.white};
    font-size: 12px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 20px;
    font-family: 'DM Sans', sans-serif;
  }

  /* Stat cards */
  .emp-stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    padding: 28px 32px 0;
  }
  .emp-stat-card {
    background: ${T.white};
    border-radius: 16px;
    padding: 20px 24px;
    border: 1px solid ${T.border};
    position: relative;
    overflow: hidden;
    transition: box-shadow 0.2s, transform 0.2s;
  }
  .emp-stat-card:hover {
    box-shadow: 0 8px 28px rgba(249,115,22,0.12);
    transform: translateY(-2px);
  }
  .emp-stat-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: ${T.orange};
    border-radius: 16px 16px 0 0;
  }
  .emp-stat-label {
    font-size: 12px;
    font-weight: 500;
    color: ${T.muted};
    text-transform: uppercase;
    letter-spacing: 0.8px;
    margin-bottom: 6px;
  }
  .emp-stat-value {
    font-family: 'Syne', sans-serif;
    font-size: 36px;
    font-weight: 800;
    color: ${T.dark};
    line-height: 1;
  }
  .emp-stat-icon {
    position: absolute;
    right: 18px;
    top: 18px;
    font-size: 28px;
    color: ${T.orange};
    opacity: 0.18;
  }

  /* Toolbar */
  .emp-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    padding: 24px 32px 0;
  }
  .emp-toolbar-right {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* Buttons */
  .emp-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 0 18px;
    height: 38px;
    border-radius: 10px;
    font-size: 13.5px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    border: none;
    transition: all 0.18s;
    white-space: nowrap;
  }
  .emp-btn-primary {
    background: ${T.orange};
    color: ${T.white};
    box-shadow: 0 2px 10px rgba(249,115,22,0.3);
  }
  .emp-btn-primary:hover { background: ${T.orangeDark}; box-shadow: 0 4px 16px rgba(249,115,22,0.4); }
  .emp-btn-outline {
    background: ${T.white};
    color: ${T.dark};
    border: 1.5px solid ${T.border};
  }
  .emp-btn-outline:hover { border-color: ${T.orange}; color: ${T.orange}; }
  .emp-btn-ghost {
    background: transparent;
    color: ${T.muted};
    border: 1.5px solid ${T.border};
  }
  .emp-btn-ghost:hover { background: ${T.orangeLight}; color: ${T.orange}; border-color: ${T.orange}; }
  .emp-btn-danger {
    background: #FEF2F2;
    color: ${T.danger};
    border: 1.5px solid #FECACA;
  }
  .emp-btn-danger:hover { background: ${T.danger}; color: ${T.white}; }

  /* Search input */
  .emp-search-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  .emp-search-wrap svg {
    position: absolute;
    left: 12px;
    color: ${T.muted};
    font-size: 15px;
    pointer-events: none;
  }
  .emp-search-input {
    padding: 0 14px 0 36px;
    height: 38px;
    border-radius: 10px;
    border: 1.5px solid ${T.border};
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    color: ${T.dark};
    width: 240px;
    outline: none;
    transition: border-color 0.18s, box-shadow 0.18s;
    background: ${T.white};
  }
  .emp-search-input:focus { border-color: ${T.orange}; box-shadow: 0 0 0 3px rgba(249,115,22,0.12); }

  /* Table wrapper */
  .emp-table-wrap {
    margin: 24px 32px 32px;
    background: ${T.white};
    border-radius: 20px;
    border: 1px solid ${T.border};
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(15,23,42,0.05);
  }

  /* Ant table overrides */
  .emp-table-wrap .ant-table {
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
  }
  .emp-table-wrap .ant-table-thead > tr > th {
    background: ${T.dark} !important;
    color: ${T.white} !important;
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 12.5px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    border-bottom: none !important;
    padding: 14px 18px !important;
  }
  .emp-table-wrap .ant-table-thead > tr > th::before { display: none !important; }
  .emp-table-wrap .ant-table-tbody > tr > td {
    padding: 14px 18px !important;
    border-bottom: 1px solid #F1F5F9 !important;
    color: ${T.dark};
    vertical-align: middle;
  }
  .emp-table-wrap .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
  .emp-table-wrap .ant-table-tbody > tr:hover > td {
    background: ${T.orangeLight} !important;
  }
  .emp-table-wrap .ant-table-tbody > tr:nth-child(even) > td {
    background: #FAFAFA;
  }
  .emp-table-wrap .ant-table-tbody > tr:nth-child(even):hover > td {
    background: ${T.orangeLight} !important;
  }
  .emp-table-wrap .ant-pagination {
    padding: 16px 24px !important;
    margin: 0 !important;
    border-top: 1px solid ${T.border};
  }
  .emp-table-wrap .ant-pagination-item-active {
    background: ${T.orange} !important;
    border-color: ${T.orange} !important;
  }
  .emp-table-wrap .ant-pagination-item-active a { color: ${T.white} !important; }

  /* Avatar cell */
  .emp-avatar {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: ${T.orangeLight};
    color: ${T.orange};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Syne', sans-serif;
    font-weight: 700;
    font-size: 14px;
    margin-right: 10px;
    flex-shrink: 0;
  }
  .emp-name-cell { display: flex; align-items: center; }
  .emp-name-text { font-weight: 500; }

  /* Type badges */
  .emp-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
  }

  /* Modal */
  .ant-modal-content { border-radius: 20px !important; overflow: hidden; }
  .ant-modal-header { border-bottom: 1px solid ${T.border} !important; padding: 20px 24px !important; }
  .ant-modal-title {
    font-family: 'Syne', sans-serif !important;
    font-weight: 700 !important;
    font-size: 18px !important;
    color: ${T.dark} !important;
  }
  .ant-modal-footer { border-top: 1px solid ${T.border} !important; padding: 16px 24px !important; }
  .ant-btn-primary { background: ${T.orange} !important; border-color: ${T.orange} !important; border-radius: 8px !important; }
  .ant-btn { border-radius: 8px !important; font-family: 'DM Sans', sans-serif !important; }
  .ant-form-item-label label {
    font-family: 'DM Sans', sans-serif !important;
    font-weight: 500 !important;
    color: ${T.mid} !important;
    font-size: 13px !important;
  }
  .ant-input, .ant-select-selector, .ant-input-affix-wrapper {
    border-radius: 10px !important;
    border-color: ${T.border} !important;
    font-family: 'DM Sans', sans-serif !important;
  }
  .ant-input:focus, .ant-select-focused .ant-select-selector {
    border-color: ${T.orange} !important;
    box-shadow: 0 0 0 3px rgba(249,115,22,0.12) !important;
  }
`;

/* ─── Badge color map ────────────────────────────────────── */
const badgeStyle = {
  "House Keeping":              { bg: "#EFF6FF", color: "#2563EB" },
  "Security":                   { bg: "#F0FDF4", color: "#16A34A" },
  "Kitchen Staff":              { bg: "#FFF7ED", color: "#EA580C" },
  "Computer Staff":             { bg: "#FAF5FF", color: "#7C3AED" },
  "Information Technology Staff": { bg: "#F0F9FF", color: "#0284C7" },
  "Social Media Staff":         { bg: "#FDF4FF", color: "#A21CAF" },
  "Administrative Staff":       { bg: "#F0FDFA", color: "#0F766E" },
  "Maintenance Staff":          { bg: "#FFF1F2", color: "#E11D48" },
  "Other":                      { bg: "#F8FAFC", color: "#64748B" },
};

/* ─── Main component ─────────────────────────────────────── */
const EmployeeManagement = () => {
  const [employees, setEmployees]               = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [isModalVisible, setIsModalVisible]     = useState(false);
  const [editingEmployee, setEditingEmployee]   = useState(null);
  const [form]                                  = Form.useForm();
  const [searchText, setSearchText]             = useState("");
  const [selectedImage, setSelectedImage]       = useState(null);
  const tableRef                                = useRef();

  const employeeTypes = [
    "House Keeping","Security","Kitchen Staff","Computer Staff",
    "Information Technology Staff","Social Media Staff",
    "Administrative Staff","Maintenance Staff","Other",
  ];

  useEffect(() => { fetchEmployees(); }, []);

  /* ── Fetch ── */
  const fetchEmployees = async () => {
    try {
      const response = await dataService.getAllEmployee();
      const apiEmployees = (response?.data || []).filter(emp => emp.isActive !== false);
      const mapped = apiEmployees.map(emp => ({
        id: emp._id,
        name: emp.employeeName,
        employeeType: emp.employeeType,
        mobile: emp.mobileNumber,
        aadhaar: emp.aadharNumber,
        address: emp.address,
        photo: null,
      }));
      setEmployees(mapped);
      setFilteredEmployees(mapped);
    } catch {
      message.error("Failed to fetch employees");
    }
  };

  /* ── Search ── */
  const handleSearch = (e) => {
    const val = e.target.value.toLowerCase();
    setSearchText(val);
    if (!val) { setFilteredEmployees(employees); return; }
    setFilteredEmployees(employees.filter(emp =>
      emp.name.toLowerCase().includes(val) ||
      emp.employeeType.toLowerCase().includes(val) ||
      emp.mobile.includes(val) ||
      emp.address.toLowerCase().includes(val)
    ));
  };

  /* ── Export ── */
  const handleExportToExcel = () => {
    if (!employees.length) { message.warning("No data to export"); return; }
    const ws = XLSX.utils.json_to_sheet(employees.map(e => ({
      Name: e.name, "Employee Type": e.employeeType,
      Mobile: e.mobile, Aadhaar: e.aadhaar, Address: e.address,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    const d = new Date();
    XLSX.writeFile(wb, `Employees_${d.getFullYear()}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}.xlsx`);
    message.success("Exported successfully!");
  };

  const handleBulkEmployeeUpload = async (file) => {
    try {
      await dataService.uploadBulkEmployee(file);
      message.success("Employees uploaded successfully");
      fetchEmployees();
    } catch { message.error("Failed to upload employees"); }
    return false;
  };

  /* ── Modal ── */
  const showModal = (employee = null) => {
    setEditingEmployee(employee);
    if (employee) { form.setFieldsValue(employee); }
    else { form.resetFields(); setSelectedImage(null); }
    setIsModalVisible(true);
  };
  const handleCancel = () => {
    setIsModalVisible(false); setEditingEmployee(null);
    setSelectedImage(null); form.resetFields();
  };
  const handlePhotoUpload = (file) => {
    setSelectedImage(file.originFileObj || file);
    return false;
  };

  /* ── Submit ── */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        employeeName: values.name, employeeType: values.employeeType,
        mobileNumber: values.mobile, aadharNumber: values.aadhaar,
        address: values.address, selectedImage,
      };
      if (editingEmployee) {
        if (!editingEmployee.id) { message.error("Invalid employee ID"); return; }
        await dataService.editEmployee(editingEmployee.id, payload);
        message.success("Employee updated successfully!");
        handleCancel(); fetchEmployees(); return;
      }
      const response = await dataService.createEmployee(payload);
      const employeeId = response?.data?._id;
      if (selectedImage) await dataService.uploadImageEmployee(employeeId, selectedImage);
      message.success("Employee created successfully!");
      handleCancel(); fetchEmployees();
    } catch { message.error("Failed to save employee"); }
  };

  /* ── Delete ── */
  const handleDelete = async (id) => {
    try {
      if (!id) { message.error("Invalid employee ID"); return; }
      await dataService.deleteEmployee(id);
      message.success("Employee deactivated successfully!");
      fetchEmployees();
    } catch { message.error("Failed to delete employee"); }
  };

  /* ── Validators ── */
  const validateAadhaar = (_, v) => !v || /^\d{12}$/.test(v) ? Promise.resolve() : Promise.reject("Aadhaar must be 12 digits");
  const validateMobile  = (_, v) => !v || /^\d{10}$/.test(v) ? Promise.resolve() : Promise.reject("Mobile must be 10 digits");

  /* ── Stats ── */
  const totalEmployees = employees.length;
  const byType = {};
  employeeTypes.forEach(t => { byType[t] = employees.filter(e => e.employeeType === t).length; });
  const topTypes = Object.entries(byType).filter(([,c]) => c > 0).sort((a,b) => b[1]-a[1]).slice(0,3);

  /* ── Columns ── */
  const columns = [
    {
      title: "Employee",
      dataIndex: "name",
      key: "name",
      sorter: (a,b) => a.name.localeCompare(b.name),
      render: (name) => (
        <div className="emp-name-cell">
          <div className="emp-avatar">{name?.charAt(0).toUpperCase()}</div>
          <span className="emp-name-text">{name}</span>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "employeeType",
      key: "employeeType",
      filters: employeeTypes.map(t => ({ text: t, value: t })),
      onFilter: (v, r) => r.employeeType === v,
      render: (type) => {
        const s = badgeStyle[type] || badgeStyle["Other"];
        return (
          <span className="emp-badge" style={{ background: s.bg, color: s.color }}>
            {type}
          </span>
        );
      },
    },
    {
      title: "Mobile",
      dataIndex: "mobile",
      key: "mobile",
      align: "center",
      render: (v) => <span style={{ fontFamily: "monospace", fontWeight: 500 }}>{v}</span>,
    },
    {
      title: "Aadhaar",
      dataIndex: "aadhaar",
      key: "aadhaar",
      align: "center",
      render: (v) => v ? (
        <span style={{ color: T.muted, fontFamily: "monospace" }}>
          •••• •••• {String(v).slice(-4)}
        </span>
      ) : "—",
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      render: (v) => <span style={{ color: T.muted }}>{v || "—"}</span>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size={8}>
          <button className="emp-btn emp-btn-outline" style={{ height: 32, padding: "0 12px", fontSize: 13 }} onClick={() => showModal(record)}>
            <EditOutlined /> Edit
          </button>
          <Popconfirm
            title="Delete this employee?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes" cancelText="No"
          >
            <button className="emp-btn emp-btn-danger" style={{ height: 32, padding: "0 12px", fontSize: 13 }}>
              <DeleteOutlined /> Delete
            </button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <style>{globalStyles}</style>
      <div className="emp-page">

        {/* Header */}
        <div className="emp-header">
          <h1 className="emp-header-title">Employee <span>Management</span></h1>
          
        </div>

        {/* Stat cards */}
        <div className="emp-stat-grid">
          <div className="emp-stat-card">
            <div className="emp-stat-label">Total Employees</div>
            <div className="emp-stat-value">{totalEmployees}</div>
            <TeamOutlined className="emp-stat-icon" />
          </div>
          {topTypes.map(([type, count]) => (
            <div className="emp-stat-card" key={type}>
              <div className="emp-stat-label">{type}</div>
              <div className="emp-stat-value">{count}</div>
              <UserOutlined className="emp-stat-icon" />
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="emp-toolbar">
          <button className="emp-btn emp-btn-primary" onClick={() => showModal()}>
            <PlusOutlined /> Add Employee
          </button>
          <button className="emp-btn emp-btn-outline" onClick={handleExportToExcel}>
            <DownloadOutlined /> Export Excel
          </button>
          <button className="emp-btn emp-btn-outline">
            <a href="/employee.xlsx" download="Employee_Bulk_Upload_Template.xlsx"
              style={{ color: "inherit", textDecoration: "none", display: "flex", alignItems: "center", gap: 7 }}>
              <DownloadOutlined /> Sample File
            </a>
          </button>
          <Upload beforeUpload={handleBulkEmployeeUpload} showUploadList={false} accept=".xlsx,.xls">
            <button className="emp-btn emp-btn-ghost">
              <UploadOutlined /> Import Excel
            </button>
          </Upload>
          <button className="emp-btn emp-btn-ghost" onClick={() => window.print()}>
            <PrinterOutlined /> Print
          </button>
          <button className="emp-btn emp-btn-ghost" onClick={() => { fetchEmployees(); setSearchText(""); message.success("Refreshed!"); }}>
            <ReloadOutlined /> Refresh
          </button>

          <div className="emp-toolbar-right">
            <div className="emp-search-wrap">
              <SearchOutlined />
              <input
                className="emp-search-input"
                placeholder="Search employees…"
                value={searchText}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="emp-table-wrap">
          <CustomTable
            ref={tableRef}
            columns={columns}
            dataSource={filteredEmployees}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} employees` }}
            scroll={{ x: true }}
          />
        </div>

        {/* Modal */}
        <Modal
          title={editingEmployee ? "Edit Employee" : "Add New Employee"}
          visible={isModalVisible}
          onOk={handleSubmit}
          onCancel={handleCancel}
          okText={editingEmployee ? "Update" : "Add Employee"}
          width={560}
        >
          <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
            <Form.Item name="name" label="Employee Name" rules={[{ required: true, message: "Required" }]}>
              <Input placeholder="Full name" />
            </Form.Item>
            <Form.Item name="employeeType" label="Employee Type" rules={[{ required: true, message: "Required" }]}>
              <Select placeholder="Select type">
                {employeeTypes.map(t => <Option key={t} value={t}>{t}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="mobile" label="Mobile Number" rules={[{ required: true, message: "Required" }, { validator: validateMobile }]}>
              <Input placeholder="10-digit mobile" maxLength={10} />
            </Form.Item>
            <Form.Item name="aadhaar" label="Aadhaar Number" rules={[{ required: true, message: "Required" }, { validator: validateAadhaar }]}>
              <Input placeholder="12-digit Aadhaar" maxLength={12} />
            </Form.Item>
            <Form.Item name="address" label="Address" rules={[{ required: true, message: "Required" }]}>
              <TextArea rows={3} placeholder="Full address" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </>
  );
};

export default EmployeeManagement;